import React, { useState, useEffect, useRef, useCallback } from "react";
import { NavLink } from "react-router-dom";
import FooterBar from "../components/FooterBar";
import CandlesChart from "../components/CandlesChart";
import LoadingData from "../components/LoadingData";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";
const BINANCE_WS_BASE =
  process.env.REACT_APP_BINANCE_WS || "wss://stream.binance.com:9443/ws";
const BINANCE_REST_BASE =
  process.env.REACT_APP_BINANCE_REST || "https://api.binance.com";
const DEFAULT_SYMBOL = "BTCUSDT";
const DEFAULT_INTERVAL = "1m";
const INTERVALS = ["1m", "3m", "5m", "15m", "30m", "1h", "4h", "1d"];
const INITIAL_CANDLES = 300; // number fetched on change
const BUFFER_MULTIPLIER = 2; // keep INITIAL_CANDLES * BUFFER_MULTIPLIER in memory

function klineStream(symbol, interval = "1m") {
  return `${symbol.toLowerCase()}@kline_${interval}`;
}

export default function MarketPage() {
  const [symbol, setSymbol] = useState(DEFAULT_SYMBOL);
  const [interval, setKlineInterval] = useState(DEFAULT_INTERVAL);
  const [candles, setCandles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [topPairs, setTopPairs] = useState([]);

  useEffect(() => {
    fetch(`${API_URL}/pairs/top`)
      .then(res => res.json())
      .then(data => {
        if (data.pairs?.length > 0) {
          setTopPairs(data.pairs);
        }
      })
      .catch(() => {});
  }, []);
  
  // Get decimal places for current symbol
  const getDecimalPlaces = (sym) => {
    const decimals = {
      "BTCUSDT": 2, "ETHUSDT": 2, "ADAUSDT": 4, "SOLUSDT": 2, "TRXUSDT": 4,
    };
    return decimals[sym] || 2;
  };

  const wsRef = useRef(null);
  const connectingRef = useRef(false); // evita crear múltiples conexiones concurrentes
  const connectionStateRef = useRef({ connected: false, lastPong: Date.now() });
  const waitingFirstKlineRef = useRef(false);
  const subscribedRef = useRef(new Set());
  const queueRef = useRef([]);
  const idCounterRef = useRef(1);
  const reconnectRef = useRef({ tries: 0, timer: null });
  const abortFetchRef = useRef(null);

  const MAX_BUFFER = INITIAL_CANDLES * BUFFER_MULTIPLIER;

  const buildWsUrl = useCallback(() => BINANCE_WS_BASE, []);

  const sendJson = useCallback((obj) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      queueRef.current.push(obj);
      return false;
    }
    try {
      ws.send(JSON.stringify(obj));
      return true;
    } catch (e) {
      queueRef.current.push(obj);
      return false;
    }
  }, []);

  const subscribeTo = useCallback(
    (streams) => {
      if (!Array.isArray(streams)) streams = [streams];
      const req = {
        method: "SUBSCRIBE",
        params: streams,
        id: idCounterRef.current++,
      };
      sendJson(req);
      streams.forEach((s) => subscribedRef.current.add(s));
    },
    [sendJson],
  );

  const unsubscribeFrom = useCallback(
    (streams) => {
      if (!Array.isArray(streams)) streams = [streams];
      const req = {
        method: "UNSUBSCRIBE",
        params: streams,
        id: idCounterRef.current++,
      };
      sendJson(req);
      streams.forEach((s) => subscribedRef.current.delete(s));
    },
    [sendJson],
  );

  const flushQueue = useCallback(() => {
    const q = queueRef.current.splice(0, queueRef.current.length);
    q.forEach((obj) => {
      try {
        wsRef.current?.send(JSON.stringify(obj));
      } catch (e) {
        /* ignore */
      }
    });
  }, []);

  const resetReconnect = useCallback(() => {
    reconnectRef.current.tries = 0;
    if (reconnectRef.current.timer) {
      clearTimeout(reconnectRef.current.timer);
      reconnectRef.current.timer = null;
    }
  }, []);

  const scheduleReconnect = useCallback(() => {
    const tries = ++reconnectRef.current.tries;
    const backoff = Math.min(30_000, 500 * 2 ** (tries - 1));
    if (reconnectRef.current.timer) clearTimeout(reconnectRef.current.timer);
    reconnectRef.current.timer = setTimeout(
      () => {
        connectWs();
      },
      backoff + Math.random() * 200,
    );
  }, []);

  // fetch historical klines from Binance REST
  const fetchInitialCandles = useCallback(
    async (sym, intv, limit = INITIAL_CANDLES) => {
      const usedInterval = intv || DEFAULT_INTERVAL;
      try {
        if (abortFetchRef.current) {
          abortFetchRef.current.abort();
        }
        const ac = new AbortController();
        abortFetchRef.current = ac;
        const url = `${BINANCE_REST_BASE}/api/v3/klines?symbol=${encodeURIComponent(sym)}&interval=${encodeURIComponent(usedInterval)}&limit=${limit}`;
        const res = await fetch(url, { signal: ac.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        // data is array of arrays
        const parsed = data.map((d) => ({
          time: Math.floor(d[0] / 1000),
          open: Number(d[1]),
          high: Number(d[2]),
          low: Number(d[3]),
          close: Number(d[4]),
          volume: Number(d[5]),
          isFinal: true,
        }));
        // keep only last MAX_BUFFER elements if API returns more
        const sliced = parsed.slice(-MAX_BUFFER);
        setCandles(sliced);
        setLoading(false);
        waitingFirstKlineRef.current = false;
        return sliced;
      } catch (err) {
        if (err?.name === "AbortError") return [];
        console.warn("fetchInitialCandles error", err);
        setLoading(false);
        waitingFirstKlineRef.current = false;
        return [];
      } finally {
        abortFetchRef.current = null;
      }
    },
    [MAX_BUFFER],
  );

  // handle incoming kline messages and maintain buffer cap
  const handleMessage = useCallback(
    (raw) => {
      let msg = raw;
      try {
        msg = JSON.parse(raw);
      } catch (e) {
        /* keep raw */
      }

      const kobj = msg?.k || msg?.data?.k;
      if (kobj) {
        const candle = {
          time: Math.floor(Number(kobj.t) / 1000),
          open: Number(kobj.o),
          high: Number(kobj.h),
          low: Number(kobj.l),
          close: Number(kobj.c),
          volume: Number(kobj.v),
          isFinal: !!kobj.x,
        };

        if (waitingFirstKlineRef.current) {
          waitingFirstKlineRef.current = false;
          setLoading(false);
        }

        setCandles((prev) => {
          const copy = prev.slice();
          if (!copy.length || copy[copy.length - 1].time < candle.time) {
            copy.push(candle);
          } else {
            copy[copy.length - 1] = candle;
          }
          // enforce buffer max length
          if (copy.length > MAX_BUFFER) {
            // remove oldest until length == MAX_BUFFER
            const remove = copy.length - MAX_BUFFER;
            copy.splice(0, remove);
          }
          return copy;
        });

        return;
      }
    },
    [MAX_BUFFER],
  );

  const onOpen = useCallback(() => {
    connectionStateRef.current.connected = true;
    connectionStateRef.current.lastPong = Date.now();
    flushQueue();
    resetReconnect();

    const k = klineStream(symbol, interval);
    const current = Array.from(subscribedRef.current);
    const desired = new Set([k]);

    const toUnsub = current.filter((s) => !desired.has(s));
    if (toUnsub.length) unsubscribeFrom(toUnsub);

    const toSub = Array.from(desired).filter(
      (s) => !subscribedRef.current.has(s),
    );
    if (toSub.length) subscribeTo(toSub);
  }, [
    flushQueue,
    resetReconnect,
    symbol,
    interval,
    subscribeTo,
    unsubscribeFrom,
  ]);

  const onClose = useCallback(() => {
    connectionStateRef.current.connected = false;
    subscribedRef.current.clear();
    scheduleReconnect();
  }, [scheduleReconnect]);

  const onError = useCallback(() => {}, []);

  const connectWs = useCallback(() => {
    // si ya hay una conexión abierta o en proceso, no crear otra
    const wsExisting = wsRef.current;
    if (
      wsExisting &&
      (wsExisting.readyState === WebSocket.OPEN || connectingRef.current)
    ) {
      return;
    }

    // marca intento de conexión
    connectingRef.current = true;

    // crea websocket
    const url = buildWsUrl();
    let ws;
    try {
      ws = new WebSocket(url);
    } catch (err) {
      // fallo inmediato al crear -> schedule reconnect
      connectingRef.current = false;
      scheduleReconnect();
      return;
    }

    // asigna referencia
    wsRef.current = ws;

    let opened = false;
    // timeout: si no abre en X ms, cierre y reintentar
    const connTimeout = setTimeout(() => {
      if (!opened && wsRef.current === ws) {
        try {
          ws.close();
        } catch (e) {}
      }
    }, 8000);

    const handleOpen = () => {
      if (wsRef.current !== ws) return; // evento de socket antiguo
      opened = true;
      connectingRef.current = false;
      onOpen();
    };

    const handleMessageEvent = (ev) => {
      if (wsRef.current !== ws) return;
      connectionStateRef.current.lastPong = Date.now();
      handleMessage(ev.data);
    };

    const handleErrorEvent = (ev) => {
      if (wsRef.current !== ws) return;
      // no hacer reconnect directo aquí; onclose se encargará
      console.warn("WS error", ev);
    };

    const handleCloseEvent = (ev) => {
      if (wsRef.current !== ws) return;
      connectingRef.current = false;
      clearTimeout(connTimeout);
      // limpiar referencia si es el actual
      if (wsRef.current === ws) wsRef.current = null;
      onClose();
    };

    ws.addEventListener("open", handleOpen);
    ws.addEventListener("message", handleMessageEvent);
    ws.addEventListener("error", handleErrorEvent);
    ws.addEventListener("close", handleCloseEvent);
  }, [buildWsUrl, handleMessage, onClose, onError, onOpen]);

  // initial connect and cleanup
  useEffect(() => {
    connectWs();
    const healthInterval = window.setInterval(() => {
      const now = Date.now();
      if (!connectionStateRef.current.connected) return;
      if (now - connectionStateRef.current.lastPong > 60_000) {
        try {
          // solo cerrar si ya está conectado (no durante CONNECTING)
          if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN)
            wsRef.current.close();
        } catch (e) {
          /* ignore */
        }
      }
    }, 10_000);

    return () => {
      window.clearInterval(healthInterval);
      if (reconnectRef.current.timer) clearTimeout(reconnectRef.current.timer);
      try {
        const subs = Array.from(subscribedRef.current);
        if (
          subs.length &&
          wsRef.current &&
          wsRef.current.readyState === WebSocket.OPEN
        ) {
          const req = {
            method: "UNSUBSCRIBE",
            params: subs,
            id: idCounterRef.current++,
          };
          wsRef.current.send(JSON.stringify(req));
        }
      } catch (e) {
        /* ignore */
      }
      try {
        // remove and close safely
        if (wsRef.current) {
          try {
            wsRef.current.close();
          } catch (e) {}
          wsRef.current = null;
        }
      } catch (e) {
        /* ignore */
      }
      subscribedRef.current.clear();
      queueRef.current = [];
      if (abortFetchRef.current) abortFetchRef.current.abort();
      abortFetchRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once

  // react to symbol or interval changes: fetch historical then ensure subscription and loading state
  useEffect(() => {
    let mounted = true;
    const desiredK = klineStream(symbol, interval);

    // prepare loading and reset candles
    waitingFirstKlineRef.current = true;
    setLoading(true);
    setCandles([]);

    // fetch historical candles, then ensure subscription (subscribe logic will be triggered in onOpen or below)
    (async () => {
      await fetchInitialCandles(symbol, interval, INITIAL_CANDLES);
      if (!mounted) return;
      // ensure websocket subscribed to desired kline
      const current = Array.from(subscribedRef.current);
      const desired = new Set([desiredK]);
      const toUnsub = current.filter((s) => !desired.has(s));
      if (toUnsub.length) unsubscribeFrom(toUnsub);
      const toSub = Array.from(desired).filter(
        (s) => !subscribedRef.current.has(s),
      );
      if (toSub.length) subscribeTo(toSub);
    })();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, interval]);

  return (
    <>
      <nav className="header-nav">
        <NavLink
          to="/"
          className="logo"
          style={{ fontWeight: 700, marginRight: 16 }}
        >
          SUMABIT
        </NavLink>
      </nav>
      <br />
      <br />
      <section
        style={{
          padding: 20,
          display: "block",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div style={{ gap: 12, alignItems: "center", display: "flex" }}>
          <label>
            Mercado:
            <select
              value={symbol}
              onChange={(e) => setSymbol(e.target.value)}
              style={{ marginLeft: 8 }}
            >
              {topPairs.length === 0 && (
                <>
                  <option value="BTCUSDT">BTC/USDT</option>
                  <option value="ETHUSDT">ETH/USDT</option>
                  <option value="ADAUSDT">ADA/USDT</option>
                  <option value="SOLUSDT">SOL/USDT</option>
                  <option value="TRXUSDT">TRX/USDT</option>
                </>
              )}
              {topPairs.map((p) => {
                const sym = p.symbol.replace("USDT", "/USDT");
                return (
                  <option key={p.symbol} value={p.symbol}>
                    {sym}
                  </option>
                );
              })}
            </select>
          </label>

          <label>
            Timeline:
            <select
              value={interval}
              onChange={(e) => setKlineInterval(e.target.value)}
              style={{ marginLeft: 8 }}
            >
              {INTERVALS.map((it) => (
                <option key={it} value={it}>
                  {it}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div style={{ width: "96%", margin: "12px auto", marginTop: 12 }}>
          {loading && <LoadingData text="Cargando velas..." />}

          <div style={{ marginTop: 8 }}>
            <CandlesChart
              candles={candles}
              superTrendPeriod={10}
              superTrendMultiplier={3}
              height={620}
              decimalPlaces={getDecimalPlaces(symbol)}
            />
          </div>
        </div>
      </section>

      <FooterBar />
    </>
  );
}
